import PendenciasProcessoService from '@/services/PendenciasProcessoService';
import ControleGabineteService from '@/services/ControleGabineteService';
import ControleGabineteStore from '@/store/ControleGabineteStore';
import GabineteStore from '@/store/GabineteStore';
import MensagemStore from '@/store/MensagemStore';
import MovimentacaoInternaStore from '@/store/MovimentacaoInternaStore';
import { Close, CloudDownload, Help, KeyboardArrowLeftRounded, KeyboardArrowRightRounded, Search, SentimentSatisfiedAltOutlined, TableView, Warning } from '@mui/icons-material';
import { Button, Checkbox, CircularProgress, Divider, Fab, FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Select, TextField, Toolbar, Tooltip, Typography } from '@mui/material';
import { action, computed, observable, runInAction, makeObservable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import Modal from '../../misc/Modal';
import { ProcessoExterno } from '../../modelo/ProcessoExterno';
import ImportacaoService from '../../services/ImportacaoService';
import ParametroSistemaService from '../../services/ParametroSistemaService';
import UsuarioStore from '../../store/UsuarioStore';
import FiltroImportacaoStore from '../../store/FiltroImportacaoStore';
import ImportacaoProcessosStore from '../../store/ImportacaoProcessosStore';
import PaginacaoImportacaoStore from '../../store/PaginacaoImportacaoStore';
import ParametroStore from '../../store/ParametroStore';
import FiltrosImportacao from '../filtrosImportacao/FiltrosImportacao';
import SelectServidores from '../SelectServidores';
import Botao from '../controle-gabinete/Botao';
import './css/botaoImportar.css';
import PaginacaoModalImportacao from './PaginacaoModalImportacao';
import ImportacaoLoteCSV from './ImportacaoLoteCSV';
import ProgressoImportacaoLote from './ProgressoImportacaoLote';
import { TipoControle } from '@/modelo';
import ProcessoService from '@/services/ProcessoService';
import UtilNumeroProcesso from '@/utils/UtilNumeroProcesso';

interface IPropsBotaoImportar {
    processosParaImportar: ProcessoExterno[];
    forcarImportar: boolean;
    origemImportacaoGE: string;
    modoIcone?: boolean;
}

class BotaoImportar extends React.Component<IPropsBotaoImportar> {
    processoJaImportadoNoGabinete = false;
    preparandoImportacao = false;
    iniciandoImportacao = false;
    pesquisandoProcessos = false;
    verificacaoImportacaoConcluida = !this.props.modoIcone;

    importacaoLoteRef = React.createRef<React.ComponentRef<typeof ImportacaoLoteCSV>>();

    componentDidMount() {
        // Precisa ser síncrono (não pode retornar Promise): em React 19 + mobx-react 9,
        // um componentDidMount `async` quebra permanentemente a reatividade do observer().
        (async () => {
            if (!this.props.processosParaImportar?.length) {
                return;
            }
            const processoImportadoNoGabinete = await ProcessoService.recuperaPorSearch({
                gabinete: UsuarioStore.gabinete,
                idsProcessos: [this.props.processosParaImportar[0].identificadorProcesso],
            });
            runInAction(() => {
                if (processoImportadoNoGabinete?.length > 0) {
                    this.processoJaImportadoNoGabinete = true;
                }
                this.verificacaoImportacaoConcluida = true;
            });
            this.forceUpdate();
        })();
    };

    constructor(props: IPropsBotaoImportar) {
        super(props);

        makeObservable(this, {
            processoJaImportadoNoGabinete: observable,
            preparandoImportacao: observable,
            iniciandoImportacao: observable,
            pesquisandoProcessos: observable,
            verificacaoImportacaoConcluida: observable,
            controlesGabMenuItemLote: computed,
            processosSelecionados: computed,
            labelImportarProcessosSelecionados: computed,
            tooltipImportarTodos: computed,
            existePendencia: computed,
            handleChangeServidorDropDownLote: action,
            importarNovo: action,
            cancelar: action,
            handleChangeProximoCardLote: action,
            abrirModalImportacao: action,
            pesquisarProcessos: action,
            onChangeListaProcessos: action,
            toggleImportacaoAutomatica: action,
        });
    }

    get forcarImportacao() {
        return this.verificacaoImportacaoConcluida
            && this.props.forcarImportar
            && !this.processoJaImportadoNoGabinete;
    }

    importarTodos = async () => {
        runInAction(() => {
            this.iniciandoImportacao = true;
        });
        try {
            await ImportacaoService.importarTodosProcessos(this.props.origemImportacaoGE, ImportacaoProcessosStore.servidorAtribuidoLote, ImportacaoProcessosStore.controleGabAtribuidoLote);
            this.fecharModalParaImportacao();
        } finally {
            runInAction(() => {
                this.iniciandoImportacao = false;
            });
        }
    };

    fecharModalParaImportacao = () => {
        runInAction(() => {
            ImportacaoProcessosStore.abertoModalImportacao = false;
            FiltroImportacaoStore.limparFiltroNumero();
        });
    };

    handleChangeServidorDropDownLote = (event) => {
        runInAction(() => {
            ImportacaoProcessosStore.servidorAtribuidoLote = event.target.value;
        });
        this.forceUpdate();
    };

    naoExistemProcessos = (): boolean => {
        return ImportacaoProcessosStore.processosQuePodemSerImportadosFiltrado.length === 0;
    };

    importarNovo = async () => {
        if (this.processosSelecionados.some(p => p.pendencia)) {
            MensagemStore.apresentarAviso('Há algum processo com pendência que impede a importação para a tarefa selecionada');
        }
        runInAction(() => {
            this.iniciandoImportacao = true;
        });
        try {
            await ImportacaoService.importarProcessos(this.props.origemImportacaoGE, ImportacaoProcessosStore.servidorAtribuidoLote, ImportacaoProcessosStore.controleGabAtribuidoLote);
            this.fecharModalParaImportacao();
        } finally {
            runInAction(() => {
                this.iniciandoImportacao = false;
            });
        }
    };

    cancelar = (_event?, reason?) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
        }
        runInAction(() => {
            ImportacaoProcessosStore.abertoModalImportacao = false;
            ImportacaoProcessosStore.processosQuePodemSerImportados = [];
            ImportacaoProcessosStore.servidorAtribuidoLote = '-1';
            ImportacaoProcessosStore.controleGabAtribuidoLote = -1;
            FiltroImportacaoStore.limparFiltroNumero();
        });
        this.forceUpdate();
    };

    handleChangeProximoCardLote = (event) => {
        runInAction(() => {
            ImportacaoProcessosStore.controleGabAtribuidoLote = event.target.value;
            MovimentacaoInternaStore.proximoCard = event.target.value;
        });
        PendenciasProcessoService.pendenciasTarefa(this.processosSelecionados);
        this.forceUpdate();
    };

    get controlesGabMenuItemLote() {
        return ControleGabineteStore.listaDeControles
            .filter((card) => card.descricao !== 'Importar do PJe')
            .filter((card) => card.descricao !== 'Importar processos')
            .filter((card) => card.descricao !== 'Visto à pauta')
            .filter((card) => card.descricao !== 'Aguardando julgamento')
            .filter((card) => card.descricao !== 'Aguardando Julgamento')
            .filter((card) => card.descricao !== 'Para assinatura')
            .filter((card) => card.configControle.regrasPossuiMinuta.length === 0)
            .map((card, index) => (
                <MenuItem value={card.numero} key={index}>
                    {card.descricao}
                </MenuItem>
            ));
    }

    abrirModalImportacao = async () => {
        FiltroImportacaoStore.inicializar();
        PaginacaoImportacaoStore.reiniciar();
        if (this.props.processosParaImportar?.length > 0) {
            runInAction(() => {
                ImportacaoProcessosStore.processosQuePodemSerImportados = this.props.processosParaImportar;
                ImportacaoProcessosStore.carregamentoProcessosConcluido = true;
            });
        } else {
            ImportacaoService.carregarProcessosParaImportar();
        }
        runInAction(() => {
            ImportacaoProcessosStore.abertoModalImportacao = true;
            const cardDistribuir = ControleGabineteStore.buscarPorTipo(TipoControle.DISTRIBUIR);
            if (cardDistribuir) {
                ImportacaoProcessosStore.controleGabAtribuidoLote = cardDistribuir.numero;
            }
        });

        if (this.props.modoIcone) {
            runInAction(() => {
                this.preparandoImportacao = true;
            });

            try {
                await Promise.all([
                    ControleGabineteStore.controlesCarregados
                        ? Promise.resolve()
                        : ControleGabineteService.atualizarControlesGabinete(),
                    ParametroStore.parametrosCarregados
                        ? Promise.resolve()
                        : ParametroStore.inicializar(),
                    GabineteStore.listaUsuariosGabinete.length > 0
                        ? Promise.resolve()
                        : GabineteStore.atualizarUsuariosGabinete()
                ]);

                runInAction(() => {
                    GabineteStore.listaUsuariosOrdenadoGabinete = GabineteStore.recuperarMinistrosServidoresOrdenados();
                    const cardDistribuir = ControleGabineteStore.buscarPorTipo(TipoControle.DISTRIBUIR);
                    ImportacaoProcessosStore.controleGabAtribuidoLote = cardDistribuir?.numero || -1;
                });
            } finally {
                runInAction(() => {
                    this.preparandoImportacao = false;
                });
            }
        }

        this.forceUpdate();
    };

    onChangeListaProcessos = (ev) => {
        const numerosFiltro = ev.target.value;
        runInAction(() => {
            FiltroImportacaoStore.numerosFiltro = numerosFiltro;
        });

        if (!numerosFiltro.trim() && FiltroImportacaoStore.numerosFiltroAplicado) {
            FiltroImportacaoStore.limparFiltroNumero();
        }
        this.forceUpdate();
    };

    limparFiltroNumeroAoSair = () => {
        if (!FiltroImportacaoStore.numerosFiltro.trim() && FiltroImportacaoStore.numerosFiltroAplicado) {
            FiltroImportacaoStore.limparFiltroNumero();
        }
    };

    pesquisarProcessos = async () => {
        const numerosCompletos = ImportacaoService.extrairNumerosCompletos(FiltroImportacaoStore.numerosFiltro);
        const numerosJaListados = new Set(
            ImportacaoProcessosStore.processosQuePodemSerImportados
                .map(processo => UtilNumeroProcesso.matchObjetoNumeroProcesso(processo.numeroProcesso).numeroCompleto)
        );
        const numerosAusentes = numerosCompletos.filter(numero => !numerosJaListados.has(numero));

        if (numerosAusentes.length > 0) {
            runInAction(() => {
                this.pesquisandoProcessos = true;
            });

            try {
                const processosEncontrados = await ImportacaoService.buscarProcessosImportaveisPorNumero(numerosAusentes);
                runInAction(() => {
                    const idsJaAdicionados = new Set(
                        ImportacaoProcessosStore.processosQuePodemSerImportados
                            .map(processo => Number(processo.identificadorProcesso))
                    );
                    ImportacaoProcessosStore.processosQuePodemSerImportados.push(
                        ...processosEncontrados.filter(processo => !idsJaAdicionados.has(Number(processo.identificadorProcesso)))
                    );
                });
            } catch (erro) {
                console.error(erro);
                MensagemStore.apresentarErro('Não foi possível verificar os processos informados.');
            } finally {
                runInAction(() => {
                    this.pesquisandoProcessos = false;
                });
            }
        }

        FiltroImportacaoStore.procurar(true);
        this.forceUpdate();
    };

    toggleImportacaoAutomatica = () => {
        const paramImportacao = ParametroStore.parametrosSistema.find(param => param.nome === 'GabsImportacaoAutomatica');
        const gabinetes = paramImportacao.valor.split(',');
        const indexGab = gabinetes.findIndex(gab => gab === UsuarioStore.gabinete);
        if (indexGab >= 0) {
            gabinetes.splice(indexGab, 1).join(',');
        } else {
            gabinetes.splice(0, 0, UsuarioStore.gabinete).join(',');
        }
        paramImportacao.valor = gabinetes.join(',');
        ParametroSistemaService.salvar(paramImportacao);
        this.forceUpdate();
    };

    get processosSelecionados() {
        return ImportacaoProcessosStore.processosQuePodemSerImportados.filter(p => p.selecionado);
    }

    get labelImportarProcessosSelecionados() {
        const quantidade = this.processosSelecionados.length;
        const processo = quantidade > 1 ? 'processos' : 'processo';
        return `Importar ${quantidade} ${processo}`;
    }

    get tooltipImportarTodos() {
        const quantidade = ImportacaoProcessosStore.processosQuePodemSerImportadosFiltrado.length;
        const processo = quantidade === 1 ? 'processo' : 'processos';
        return `${quantidade} ${processo}`;
    }

    get existePendencia() {
        return this.processosSelecionados.find(p => p.pendencia);
    }

    conferePendencia = () => {
        PendenciasProcessoService.pendenciasTarefa(this.processosSelecionados);
        this.forceUpdate();
    };

    abrirImportacaoLote = () => {
        this.importacaoLoteRef.current?.abrirModal();
    };

    render() {
        return (
            <div title={this.props.modoIcone ? undefined : 'Importar processos'} id="importarProcessos">
                {this.props.origemImportacaoGE === 'Controle de Gabinete' ? (
                    <div className='divFlexColunm aCenter' style={{ gap: '16px', display: 'flex', flexDirection: 'column' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="medium"
                            startIcon={<CloudDownload />}
                            onClick={this.abrirModalImportacao}
                            sx={{
                                width: '200px',
                                height: '40px',
                                borderRadius: '50px',
                                textTransform: 'none',
                                fontWeight: 600,
                                paddingX: '18px',
                                paddingY: '8px',
                                boxShadow: 'none',
                                '&:hover': {
                                    boxShadow: 'none',
                                }
                            }}
                        >
                            {this.props.forcarImportar ? 'Forçar importação' : 'Importar processos'}
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="medium"
                            startIcon={<TableView />}
                            onClick={this.abrirImportacaoLote}
                            sx={{
                                width: '200px',
                                height: '40px',
                                borderRadius: '50px',
                                textTransform: 'none',
                                fontWeight: 600,
                                paddingX: '10px',
                                paddingY: '8px',
                                whiteSpace: 'nowrap',
                                boxShadow: 'none',
                            }}
                        >
                            Importação Avançada
                        </Button>
                    </div>
                ) : (
                    this.forcarImportacao && (
                        this.props.modoIcone ?
                            <Tooltip title='Importar processo'>
                                <Button
                                    color='inherit'
                                    onClick={this.abrirModalImportacao}
                                    aria-label='Importar processo'
                                    startIcon={<CloudDownload />}
                                    sx={{
                                        marginRight: '8px',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        padding: '8px 18px',
                                        borderRadius: '50px',
                                        border: '1px solid rgba(255, 255, 255, 0.7)'
                                    }}
                                >
                                    Importar processo
                                </Button>
                            </Tooltip>
                            :
                            <Botao
                                label={this.props.forcarImportar ? 'Forçar Importação' : 'Importar'}
                                redirectTo={''}
                                pathnameConfig={['/adicionarControleGabinete', '/configuracaoGabinete']}
                                handleOnTouchTap={this.abrirModalImportacao}
                            />
                    )
                )}
                <Modal
                    aberto={ImportacaoProcessosStore.abertoModalImportacao}
                    fecharModal={this.cancelar}
                    titulo="Processos prontos para importação"
                    funcaoAcaoSecundaria={this.cancelar}
                    labelAcaoSecundaria="Cancelar"
                    funcaoAcaoPrimaria={this.importarNovo}
                    labelAcaoPrimaria={this.labelImportarProcessosSelecionados}
                    acaoPrimariaDisabled={this.preparandoImportacao || this.iniciandoImportacao || this.processosSelecionados.length === 0}
                    acaoSecundariaDisabled={this.iniciandoImportacao}
                    fullWidth
                    maxWidth="md"
                    PaperProps={{
                        className: 'modalImportacaoProcessos',
                        style: { height: '100%' }
                    }}
                    dialogContentStyle={{
                        overflowY: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        paddingBottom: 0
                    }}
                    dialogActionsStyle={{
                        paddingTop: '4px',
                        paddingBottom: '15px'
                    }}
                    outrasAcoes={
                        <Tooltip title={this.tooltipImportarTodos}>
                            <span>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={this.importarTodos}
                                    disabled={this.preparandoImportacao || this.iniciandoImportacao || this.naoExistemProcessos()}
                                >
                                    {FiltroImportacaoStore.filtroAplicado ? 'Importar todos filtrados' : 'Importar todos'}
                                </Button>
                            </span>
                        </Tooltip>
                    }
                >
                    {(this.preparandoImportacao || this.iniciandoImportacao) &&
                        <div className='divFlexRow aCenter' style={{ gap: '10px', marginBottom: '12px' }}>
                            <CircularProgress size={20} />
                            <Typography>{this.iniciandoImportacao ? 'Iniciando importação...' : 'Preparando importação...'}</Typography>
                        </div>
                    }
                    <div style={{ marginTop: '8px' }}>
                        <Tooltip title='A quantidade de processos importados dependerá da rotina automatizada executada durante a madrugada'>
                            <Typography>
                                <span className='divFlexRow aCenter'>
                                    <Checkbox color='primary' checked={ParametroStore.fazerImportacaoAutomatica} onClick={this.toggleImportacaoAutomatica} /> Ativar importação automática de processos do PJe
                                </span>
                            </Typography>
                        </Tooltip>
                    </div>
                    <Divider />
                    <div style={{ marginBottom: '8px', marginTop: '8px' }}>
                        <Typography >
                            Importar processos para:
                        </Typography>
                        <div className='divDistribuicao'>
                            <SelectServidores
                                handleChangeServidorDropDown={this.handleChangeServidorDropDownLote}
                                servidorAtribuido={ImportacaoProcessosStore.servidorAtribuidoLote}
                                movimentacaoIncompleta={true}
                                lote={true}
                                naoMostrarOpcaoManterResponsavel={true}
                            />
                            <FormControl fullWidth>
                                <InputLabel id="labelMovimentacao">
                                    <div className='divFlexRow aCenter'>
                                        Movimentacao
                                        {this.existePendencia &&
                                            <div style={{ zoom: '75%' }}>
                                                <Tooltip title={this.existePendencia.pendencia} >
                                                    <Warning color='secondary' />
                                                </Tooltip>
                                            </div>
                                        }
                                    </div>
                                </InputLabel>
                                <Select
                                    label='Movimentacao'
                                    labelId='labelMovimentacao'
                                    id={'dropDownMenuCards'}
                                    value={ImportacaoProcessosStore.controleGabAtribuidoLote}
                                    onChange={this.handleChangeProximoCardLote}
                                >
                                    {this.controlesGabMenuItemLote}
                                </Select>
                            </FormControl>
                        </div>
                    </div>
                    <Divider />
                    <div style={{ marginTop: '8px' }}>
                        {!ImportacaoProcessosStore.isCarregandoProcessos && <FiltrosImportacao />}
                    </div>
                    <div className='divFlexRow aCenter'>
                        <TextField multiline
                            variant="outlined"
                            label={'Lista de processos'}
                            style={{ flexGrow: 1 }}
                            value={FiltroImportacaoStore.numerosFiltro}
                            minRows={1}
                            maxRows={4}
                            onChange={this.onChangeListaProcessos}
                            onBlur={this.limparFiltroNumeroAoSair}
                            InputProps={{
                                endAdornment:
                                    <InputAdornment position="end">
                                        {FiltroImportacaoStore.numerosFiltro &&
                                            <Tooltip title="Limpar número do processo">
                                                <IconButton
                                                    size="small"
                                                    onClick={FiltroImportacaoStore.limparFiltroNumero}
                                                    aria-label="Limpar número do processo"
                                                >
                                                    <Close />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                        <Tooltip title="Separe os números de processo com ;">
                                            <Help />
                                        </Tooltip>
                                    </InputAdornment>
                            }}
                        />
                        <Fab
                            sx={{ ml: '16px', h: '40px' }}
                            color="primary"
                            size="small"
                            title="Filtrar"
                            onClick={this.pesquisarProcessos}
                            disabled={!ImportacaoProcessosStore.carregamentoProcessosConcluido || this.pesquisandoProcessos}
                        >
                            {this.pesquisandoProcessos ? <CircularProgress size={22} /> : <Search />}
                        </Fab>
                    </div>
                    <div className="conteudoPaperImportacao divFlexColumn">
                        {this.iniciandoImportacao ?
                            <div className="estadoVazioImportacao">
                                <CircularProgress size={32} />
                            </div>
                            : ImportacaoProcessosStore.processosQuePodemSerImportadosFiltrado.length === 0
                                && !ImportacaoProcessosStore.isCarregandoProcessos ?
                            <div className="estadoVazioImportacao">
                                <SentimentSatisfiedAltOutlined className="iconeEstadoVazioImportacao" />
                                <Typography>
                                    Não há processos para serem importados
                                </Typography>
                            </div>
                            :
                            <PaginacaoModalImportacao onSelect={this.conferePendencia} />
                        }
                    </div>
                    <Toolbar
                        variant="dense"
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}
                    >
                        <div
                            className="paginacao-importacao"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                height: '40px'
                            }}
                        >
                            <Typography
                                className="textoPaginacao"
                                sx={{
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {'Mostrando registros de '}
                                <b className="numero-paginacao">
                                    {PaginacaoImportacaoStore.registroInicial}
                                </b>
                                {' a '}
                                <b className="numero-paginacao">
                                    {PaginacaoImportacaoStore.registroFinal}
                                </b>
                                {' de um total de '}
                                <b className="numero-paginacao">
                                    {PaginacaoImportacaoStore.qtdProcessos}
                                </b>
                                {FiltroImportacaoStore.filtroAplicado ? ' (Filtro aplicado)' : ''}
                            </Typography>
                            <Tooltip title="Voltar página">
                                <span style={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                                    <IconButton size="small"
                                        onClick={PaginacaoImportacaoStore.handlePaginaAnterior}
                                        disabled={PaginacaoImportacaoStore.pagina === 1}
                                    >
                                        <KeyboardArrowLeftRounded />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Próxima página">
                                <span style={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                                    <IconButton size="small"
                                        onClick={PaginacaoImportacaoStore.handleProximaPagina}
                                        disabled={PaginacaoImportacaoStore.pagina >= PaginacaoImportacaoStore.qtdPaginas}
                                    >
                                        <KeyboardArrowRightRounded />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </div>
                    </Toolbar>
                </Modal>
                <ImportacaoLoteCSV ref={this.importacaoLoteRef} />
                <ProgressoImportacaoLote />
            </div>
        );
    }
}

export default observer(BotaoImportar);
